import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, ArrowUpRight,
  BarChart3, Search, Lightbulb, Sparkles, Brain, RefreshCw, Link2,
  Globe, MapPin, Target, Film, ShoppingBag, Pill, Music, Gamepad2, Newspaper,
  Lock, Home, User, MousePointerClick, CheckCircle2, X, Check,
  Sprout, Hourglass, Bike, Flame, Map,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import useReveal from '../hooks/useReveal';
import { projects } from '../mock';
import heatmapImg from '../assets/heatmap.png';
import homejioImg from '../assets/homejio.png';
import promotionalOffersImg from '../assets/promotional-offers.png';
import contentRailsImg from '../assets/content-rails.png';
import userAImg from '../assets/user-a.png';
import userBImg from '../assets/user-b.png';
import profileImg from '../assets/profile.png';
import birthdayHomeImg from '../assets/birthday-home.png';
import favActorImg from '../assets/fav-actor.png';
import swiggyImg from '../assets/swiggy.png';
import facebookImg from '../assets/facebook.png';
import instaImg from '../assets/insta.png';
import gojekImg from '../assets/gojek.png';
import moviesRailImg from '../assets/personalisation/movies.png';
import shoppingRailImg from '../assets/personalisation/shopping.png';
import healthRailImg from '../assets/personalisation/health.png';
import musicRailImg from '../assets/personalisation/music.png';
import gamesRailImg from '../assets/personalisation/games.png';
import newsRailImg from '../assets/personalisation/news.png';

const SECTIONS = ['hero', 'problem', 'research', 'phase1', 'phase2', 'learnings'];
const SECTION_LABELS = ['Intro', 'Problem', 'Research', 'Phase 1', 'Phase 2', 'Learnings'];

const INTERESTS = [
  { key: 'movies',   label: 'Movies',   Icon: Film,        image: moviesRailImg   },
  { key: 'shopping', label: 'Shopping', Icon: ShoppingBag, image: shoppingRailImg },
  { key: 'health',   label: 'Health',   Icon: Pill,        image: healthRailImg   },
  { key: 'music',    label: 'Music',    Icon: Music,       image: musicRailImg    },
  { key: 'games',    label: 'Games',    Icon: Gamepad2,    image: gamesRailImg    },
  { key: 'news',     label: 'News',     Icon: Newspaper,   image: newsRailImg     },
];

export default function PersonalisationCaseStudy() {
  useReveal();
  const navigate = useNavigate();

  const project = useMemo(() => projects.find((p) => p.slug === 'personalisation-myjio'), []);
  const nextProject = useMemo(() => {
    const idx = projects.findIndex((p) => p.slug === 'personalisation-myjio');
    return projects[(idx + 1) % projects.length];
  }, []);

  const [progress, setProgress] = useState(0);
  const [activeIdx, setActiveIdx] = useState(0);
  const [selected, setSelected] = useState(new Set());

  useEffect(() => { window.scrollTo({ top: 0 }); }, []);

  useEffect(() => {
    const onScroll = () => {
      const scrollY = window.scrollY;
      const total = document.body.scrollHeight - window.innerHeight;
      setProgress(total > 0 ? Math.min(100, Math.round((scrollY / total) * 100)) : 0);
      let active = 0;
      SECTIONS.forEach((id, i) => {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top < window.innerHeight * 0.5) active = i;
      });
      setActiveIdx(active);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (i) => {
    const el = document.getElementById(SECTIONS[i]);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const toggleInterest = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  if (!project) return null;

  return (
    <div className="App">
      <Navbar />

      {/* Scroll progress bar */}
      <div
        className="fixed top-0 left-0 h-[3px] z-[60] transition-[width] duration-100"
        style={{ width: `${progress}%`, background: 'var(--terracotta)' }}
      />

      {/* Chapter sidebar dots */}
      <div className="hidden lg:flex fixed right-6 top-1/2 -translate-y-1/2 z-40 flex-col gap-3">
        {SECTIONS.map((_, i) => (
          <button
            key={i}
            onClick={() => scrollTo(i)}
            title={SECTION_LABELS[i]}
            className="group flex items-center gap-3"
            aria-label={SECTION_LABELS[i]}
          >
            <span
              className="text-[10px] tracking-[0.18em] uppercase opacity-0 group-hover:opacity-100 transition-opacity duration-200"
              style={{ color: 'var(--ink-soft)' }}
            >
              {SECTION_LABELS[i]}
            </span>
            <span
              className="rounded-full transition-all duration-300 block"
              style={{
                width: activeIdx === i ? 12 : 8,
                height: activeIdx === i ? 12 : 8,
                background: activeIdx === i ? 'var(--terracotta)' : 'var(--border)',
              }}
            />
          </button>
        ))}
      </div>

      <article className="pt-28 md:pt-32 pb-20">
        <div className="max-w-3xl mx-auto px-6">
          <Link
            to="/#work"
            className="inline-flex items-center gap-2 text-[12px] tracking-[0.14em] uppercase link-underline"
            style={{ color: 'var(--terracotta-deep)' }}
          >
            <ArrowLeft size={14} /> Back to Work
          </Link>
        </div>

        {/* HERO */}
        <section id="hero" className="max-w-3xl mx-auto px-6 pt-12 pb-16 mb-12" style={{ borderBottom: '1px solid var(--border)' }}>
          <p className="reveal text-[11px] tracking-[0.22em] uppercase mb-5" style={{ color: 'var(--terracotta)' }}>
            Case Study · MyJio · UI/UX Design
          </p>
          <h1
            className="reveal reveal-delay-1 font-display leading-[1.05] mb-6"
            style={{ fontSize: 'clamp(40px, 6vw, 64px)', color: 'var(--ink)', fontWeight: 600 }}
          >
            Making a super-app feel{' '}
            <em className="italic" style={{ color: 'var(--terracotta)' }}>personal</em>
          </h1>
          <p className="reveal reveal-delay-2 text-[16px] leading-[1.75] mb-10 max-w-[560px]" style={{ color: 'var(--ink-soft)' }}>
            Every user who opened MyJio came with one intent: recharge. Everything else—shopping, entertainment, health—existed.
            But it wasn&apos;t <em className="italic">discovered</em>. Not because it lacked value. Because it didn&apos;t feel relevant.
          </p>
          <div className="reveal reveal-delay-3 flex flex-wrap gap-2.5">
            <MetaPill accent>UI/UX Designer</MetaPill>
            <MetaPill>MyJio Super-App</MetaPill>
            <MetaPill>Personalisation System</MetaPill>
            <MetaPill>2-Phase Rollout</MetaPill>
          </div>
        </section>

        {/* Cover image */}
        <div className="max-w-6xl mx-auto px-6 mb-20">
          <div
            className="reveal rounded-[22px] overflow-hidden shadow-warm-lg relative"
            style={{ aspectRatio: '16/7', background: project.gradient }}
          >
            {project.cover && (
              <img src={project.cover} alt={project.title} className="absolute inset-0 w-full h-full object-cover" />
            )}
          </div>
        </div>

        {/* CHAPTER 01 — PROBLEM */}
        <Chapter id="problem" number="01 — The Problem">
          <H2>
            The home was static. The ecosystem wasn&apos;t{' '}
            <em className="italic" style={{ color: 'var(--terracotta)' }}>experienced</em>.
          </H2>
          <P>
            MyJio was built as a super-app—a single surface hosting JioTV, JioMart, health, games, and dozens of mini-apps.
            But most users treated it like a telecom utility. Open, recharge, close.
          </P>
          <P>
            The problem wasn&apos;t missing features. It was perception. The home showed the same structure to everyone,
            regardless of who they were or what they needed.
          </P>

          <IllustrationBox icon={BarChart3} label="Before vs After — Home surface">
            <div className="grid grid-cols-1 md:grid-cols-2 rounded-[12px] overflow-hidden" style={{ border: '1px solid var(--border)', gap: 1, background: 'var(--border)' }}>
              <BAPanel
                variant="before"
                label="Before — Static dashboard"
                items={[
                  'Same layout for every user',
                  'No adaptation to behaviour',
                  'Discovery felt like chance',
                  'Heatmap: most sections ignored',
                ]}
              />
              <BAPanel
                variant="after"
                label="After — Living surface"
                items={[
                  'Icons reorder by usage',
                  'Personalised rails appear',
                  'Search becomes predictive',
                  'Content expires + refreshes',
                ]}
              />
            </div>
          </IllustrationBox>

          <PullQuote>&ldquo;The ecosystem existed. But it wasn&apos;t experienced.&rdquo;</PullQuote>

          {/* Cold-Start sub-section */}
          <div className="reveal mt-10 mb-2">
            <p className="text-[11px] tracking-[0.22em] uppercase mb-3" style={{ color: 'var(--ink-soft)' }}>
              Bridging into Phase 1
            </p>
            <h3 className="font-display leading-[1.2] mb-5" style={{ fontSize: 'clamp(22px, 2.6vw, 30px)', color: 'var(--ink)', fontWeight: 600 }}>
              Solving the <em className="italic" style={{ color: 'var(--terracotta)' }}>cold-start</em> problem.
            </h3>
            <P>Personalisation relies on data.</P>
            <P>New users have none.</P>
            <P>This created a tradeoff:</P>
            <ul className="space-y-2.5 mb-5 pl-1">
              <li className="flex items-start gap-3 text-[15.5px] leading-[1.7]" style={{ color: 'var(--ink-soft)' }}>
                <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: 'var(--terracotta)' }} />
                Ask for more information and increase setup effort.
              </li>
              <li className="flex items-start gap-3 text-[15.5px] leading-[1.7]" style={{ color: 'var(--ink-soft)' }}>
                <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: 'var(--terracotta)' }} />
                Ask for too little and reduce recommendation quality.
              </li>
            </ul>
            <P>We identified three high-signal inputs that could improve relevance while keeping onboarding lightweight.</P>
          </div>

          <IllustrationBox icon={Home} label="Diagnosis & solution — at a glance">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <OnboardingScreen title="Heatmap" sub="Where attention actually went" image={heatmapImg} />
              <OnboardingScreen title="Birthday Confirmation" sub="Immediate personalised payoff" image={birthdayHomeImg} />
              <OnboardingScreen title="Profile" sub="Identity & basic setup" image={profileImg} />
              <OnboardingScreen title="Favourite actor" sub="Interest signal capture" image={favActorImg} />
            </div>
          </IllustrationBox>
        </Chapter>

        {/* CHAPTER 02 — RESEARCH */}
        <Chapter id="research" number="02 — Research">
          <H2>
            What leading apps do that MyJio{' '}
            <em className="italic" style={{ color: 'var(--terracotta)' }}>didn&apos;t</em>.
          </H2>
          <P>
            When we studied Swiggy, Gojek, Instagram, Facebook, and Amazon, one thing stood out:
            their home screens weren&apos;t fixed. They evolved—based on user behaviour, time of day, and intent.
          </P>

          <div className="space-y-4 my-6">
            <CompRow
              name="Swiggy"
              desc="Location-first onboarding. Time-of-day reordering. Contextual nudges from first session."
              image={swiggyImg}
            />
            <CompRow
              name="Facebook"
              desc="Friend & interest discovery during onboarding. The feed starts personalising from the very first session."
              image={facebookImg}
            />
            <CompRow
              name="Instagram"
              desc="Feed and Explore adapt to viewing habits, trending topics, and recency. Continuous evolution."
              image={instaImg}
            />
            <CompRow
              name="Gojek"
              desc="Dashboard density shifts on login depending on whether the user skews payment or mobility-focused."
              image={gojekImg}
            />
          </div>

          <Insight icon={Lightbulb}>
            The shift in thinking: personalisation isn&apos;t about showing <strong className="text-white font-medium">more</strong>.
            It&apos;s about showing the <strong className="text-white font-medium">right things</strong>, at the{' '}
            <strong className="text-white font-medium">right time</strong>, with the{' '}
            <strong className="text-white font-medium">least effort</strong>.
          </Insight>
        </Chapter>

        <Ornament />

        {/* CHAPTER 03 — PHASE 1 */}
        <Chapter id="phase1">
          <PhaseBadge variant="phase1" icon={Sprout}>Phase 1 — Onboarding-led Foundations</PhaseBadge>
          <H2>
            Making the app feel relevant{' '}
            <em className="italic" style={{ color: 'var(--terracotta)' }}>before the user does anything</em>.
          </H2>
          <P>
            The challenge was unique: you have no behavioural data on day one. So we designed for the first impression—capturing
            lightweight signals without ever feeling like a form.
          </P>

          <IllustrationBox icon={Sparkles} label="Signals captured at onboarding">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <SignalCard Icon={Globe}  title="Language"  desc="Preferred language seeds content + UI copy defaults" />
              <SignalCard Icon={MapPin} title="Location"  desc="Local offers, nearby plans, regional defaults" />
              <SignalCard Icon={Target} title="Interests" desc="1–2 seeds: Movies, Shopping, Health, etc." />
            </div>
          </IllustrationBox>

          <IllustrationBox icon={Lightbulb} label="Value exchange — preferences weren't collected, they were demonstrated">
            <p className="text-[15px] mb-7 leading-[1.7] max-w-[560px]" style={{ color: 'var(--ink-soft)' }}>
              Tap one or more interests below — the rails you pick stack inside the MyJio home in the order you selected them. Scroll inside the phone to browse through.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_290px] gap-8 items-start">
              <div>
                <p className="text-[14px] font-medium mb-4 tracking-[0.02em]" style={{ color: 'var(--ink)' }}>
                  Choose your interests
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {INTERESTS.map(({ key, label, Icon }) => {
                    const isSel = selected.has(key);
                    return (
                      <button
                        key={key}
                        onClick={() => toggleInterest(key)}
                        className="flex flex-col items-center justify-center gap-2.5 rounded-[14px] transition-all duration-200"
                        style={{
                          aspectRatio: '5 / 4',
                          background: isSel ? 'var(--terracotta)' : 'var(--surface)',
                          color: isSel ? '#FAF6EE' : 'var(--ink)',
                          border: `1.5px solid ${isSel ? 'var(--terracotta)' : 'var(--border)'}`,
                          boxShadow: isSel ? '0 8px 20px -10px rgba(196,98,45,0.5)' : 'none',
                        }}
                      >
                        <Icon size={26} strokeWidth={1.7} />
                        <span className="text-[15px] font-medium">{label}</span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-[12.5px] mt-5 italic" style={{ color: 'var(--ink-soft)' }}>
                  Tap multiple — the rails stack inside the phone in selection order. Scroll inside the phone to view all.
                </p>
              </div>

              {/* Phone — 360x800 frame. Whole screen scrolls; bottom nav pinned. */}
              <div className="flex items-start justify-center md:justify-end">
                <div
                  className="relative w-full max-w-[290px] rounded-[22px] shadow-warm overflow-hidden"
                  style={{ aspectRatio: '360 / 800', border: '1px solid var(--border)', background: '#FFFFFF' }}
                >
                  {/* Scrolling content */}
                  <div className="absolute inset-0 overflow-y-auto" style={{ scrollBehavior: 'smooth' }}>
                    {/* Top of MyJio home — status bar, search, icons grid, postpaid card */}
                    <div style={{ aspectRatio: '360 / 384', width: '100%', overflow: 'hidden' }}>
                      <img
                        src={homejioImg}
                        alt="MyJio home top"
                        style={{ display: 'block', width: '100%', height: 'auto' }}
                      />
                    </div>
                    {/* Stacked rail images — full-width, natural aspect, selection order */}
                    {[...selected].map((key) => {
                      const item = INTERESTS.find((i) => i.key === key);
                      if (!item || !item.image) return null;
                      return (
                        <img
                          key={item.key}
                          src={item.image}
                          alt={`${item.label} rail`}
                          className="block w-full h-auto"
                          loading="lazy"
                        />
                      );
                    })}
                    {/* Spacer so content doesn't hide behind pinned bottom nav */}
                    <div style={{ width: '100%', aspectRatio: '360 / 84' }} />
                  </div>

                  {/* Pinned bottom nav — shows the bottom 84px of homejio */}
                  <div
                    className="absolute bottom-0 left-0 right-0"
                    style={{
                      aspectRatio: '360 / 84',
                      backgroundImage: `url(${homejioImg})`,
                      backgroundSize: '100% auto',
                      backgroundPosition: 'bottom',
                      backgroundRepeat: 'no-repeat',
                      boxShadow: '0 -6px 14px -6px rgba(0,0,0,0.18)',
                    }}
                  />
                </div>
              </div>
            </div>
          </IllustrationBox>
        </Chapter>

        {/* CHAPTER 04 — PHASE 2 */}
        <Chapter id="phase2">
          <PhaseBadge variant="phase2" icon={RefreshCw}>Phase 2 — Profile &amp; Behaviour-driven Adaptation</PhaseBadge>
          <H2>
            Letting the app{' '}
            <em className="italic" style={{ color: 'var(--terracotta)' }}>evolve</em> with every interaction.
          </H2>
          <P>
            Once users started interacting, the system needed to adapt. We combined declared preferences from onboarding
            with behavioural signals—clicks, dwell time, purchases—and contextual inputs like time of day and device.
          </P>

          <IllustrationBox icon={Brain} label="The user model — three signal layers">
            <UserModelDiagram />
          </IllustrationBox>

          <IllustrationBox icon={Hourglass} label="Preventing fatigue — the freshness model">
            <p className="text-[13.5px] mb-5" style={{ color: 'var(--ink-soft)' }}>
              One risk with personalisation: it becomes repetitive. We introduced expiry windows so content stayed fresh, not stale.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
              <ExpiryCard title="Promotional offers" fill={40} sub="24-hour expiry window · Rotates daily"        image={promotionalOffersImg} />
              <ExpiryCard title="Content rails"      fill={70} sub="7-day window · Eligibility-based refresh"     image={contentRailsImg} />
            </div>
            <Insight icon={RefreshCw}>
              Relevance stayed high. Content didn&apos;t feel stale. Rotation logic + eligibility ensured users always saw
              something worth tapping.
            </Insight>
          </IllustrationBox>

          <IllustrationBox icon={User} label="Adaptive home — same app, different people">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 justify-items-center">
              <ScrollablePhone
                label="User A Dashboard"
                sub="Entertainment-forward: JioTV, Music, Games prominent"
                image={userAImg}
              />
              <ScrollablePhone
                label="User B Dashboard"
                sub="Commerce-forward: JioMart, Health, Pharmacy prominent"
                image={userBImg}
              />
            </div>
            <p className="text-[12px] text-center mt-5 italic" style={{ color: 'var(--ink-soft)' }}>
              Same app · Same version · Different people — scroll inside each phone to explore
            </p>
          </IllustrationBox>
        </Chapter>

        {/* CHAPTER 05 — LEARNINGS */}
        <Chapter id="learnings" number="05 — Learnings">
          <H2>
            What this project{' '}
            <em className="italic" style={{ color: 'var(--terracotta)' }}>changed</em> in how I think.
          </H2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 mt-6">
            <LearningCard n="01">
              Personalisation is about <strong className="font-semibold" style={{ color: 'var(--ink)' }}>relevance</strong>, not volume.
              Showing less—but the right things—works better than surfacing everything.
            </LearningCard>
            <LearningCard n="02">
              Onboarding is about <strong className="font-semibold" style={{ color: 'var(--ink)' }}>trust</strong>, not data collection.
              Show users what they get before you ask for anything.
            </LearningCard>
            <LearningCard n="03">
              <strong className="font-semibold" style={{ color: 'var(--ink)' }}>Timing</strong> matters more than placement.
              The right content at the wrong moment is still irrelevant.
            </LearningCard>
            <LearningCard n="04">
              Systems need to <strong className="font-semibold" style={{ color: 'var(--ink)' }}>adapt without overwhelming</strong>.
              Freshness windows and expiry logic keep the experience alive without noise.
            </LearningCard>
          </div>
        </Chapter>

        {/* CLOSING */}
        <section className="max-w-3xl mx-auto px-6 py-16 text-center" style={{ borderTop: '1px solid var(--border)' }}>
          <p
            className="font-display italic leading-[1.5] mb-8 mx-auto max-w-[540px]"
            style={{ fontSize: 'clamp(22px, 2.4vw, 28px)', color: 'var(--ink-soft)' }}
          >
            &ldquo;Personalisation works best when users feel understood—not observed.&rdquo;
          </p>
          <div className="flex flex-wrap justify-center gap-3.5">
            <a
              href="https://ritikaghosh.framer.website/works/personalisation"
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-[13px] transition-colors duration-300"
              style={{ background: 'var(--terracotta)', color: '#FAF6EE', border: '1.5px solid var(--terracotta)' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#A4501F'; e.currentTarget.style.borderColor = '#A4501F'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--terracotta)'; e.currentTarget.style.borderColor = 'var(--terracotta)'; }}
            >
              View detailed case study <ArrowUpRight size={15} />
            </a>
            <a
              href="https://www.figma.com/proto/F0mLmAY2QMqaGVddASVQpP/Personal-Draft?page-id=331%3A11549&node-id=356-8275"
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-[13px] transition-colors duration-300"
              style={{ background: 'transparent', color: 'var(--ink)', border: '1.5px solid var(--border)' }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--ink-soft)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; }}
            >
              Open Figma Prototype <ArrowUpRight size={15} />
            </a>
          </div>
        </section>

        {/* Next / back */}
        <div className="max-w-3xl mx-auto px-6 mt-16 pt-10 flex flex-wrap items-center justify-between gap-6" style={{ borderTop: '1px solid var(--border)' }}>
          <Link
            to="/#work"
            className="inline-flex items-center gap-2 text-[13px] tracking-[0.12em] uppercase link-underline"
            style={{ color: 'var(--ink)' }}
          >
            <ArrowLeft size={15} /> Back to all work
          </Link>
          {nextProject && (
            <Link
              to={`/case/${nextProject.slug}`}
              className="group inline-flex items-center gap-3 rounded-2xl px-5 py-4 transition-all duration-300"
              style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}
            >
              <span className="text-right">
                <span className="block text-[11px] tracking-[0.18em] uppercase" style={{ color: 'var(--sage)' }}>Next case study</span>
                <span className="block font-display text-[18px]" style={{ color: 'var(--ink)' }}>{nextProject.title}</span>
              </span>
              <ArrowRight size={18} style={{ color: 'var(--terracotta)' }} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          )}
        </div>
      </article>

      <Footer />
    </div>
  );
}

/* ---------- Building blocks ---------- */

function MetaPill({ children, accent = false }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[12px] font-medium"
      style={{
        background: accent ? 'var(--terracotta)' : 'var(--surface)',
        color: accent ? '#FAF6EE' : 'var(--ink-soft)',
        border: `1px solid ${accent ? 'var(--terracotta)' : 'var(--border)'}`,
      }}
    >
      {children}
    </span>
  );
}

function Chapter({ id, number, children }) {
  return (
    <section id={id} className="max-w-3xl mx-auto px-6 mb-24 scroll-mt-24">
      {number && (
        <p className="reveal text-[11px] tracking-[0.22em] uppercase mb-3" style={{ color: 'var(--ink-soft)' }}>
          {number}
        </p>
      )}
      {children}
    </section>
  );
}

function H2({ children }) {
  return (
    <h2
      className="reveal font-display leading-[1.2] mb-6"
      style={{ fontSize: 'clamp(26px, 3vw, 36px)', color: 'var(--ink)', fontWeight: 600 }}
    >
      {children}
    </h2>
  );
}

function P({ children }) {
  return (
    <p className="reveal text-[15.5px] leading-[1.8] mb-4" style={{ color: 'var(--ink-soft)' }}>
      {children}
    </p>
  );
}

function PullQuote({ children }) {
  return (
    <blockquote
      className="reveal my-8 px-6 py-5 rounded-r-[8px]"
      style={{ borderLeft: '3px solid var(--terracotta)', background: '#FAF0E8' }}
    >
      <p className="font-display italic text-[19px] leading-[1.55]" style={{ color: 'var(--ink)' }}>
        {children}
      </p>
    </blockquote>
  );
}

function IllustrationBox({ icon: Icon, label, children }) {
  return (
    <div className="reveal my-9 rounded-[16px] overflow-hidden" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
      <div className="flex items-center gap-2 px-5 py-3.5" style={{ borderBottom: '1px solid var(--border)' }}>
        {Icon && <Icon size={14} style={{ color: 'var(--terracotta)' }} />}
        <span className="text-[11px] tracking-[0.14em] uppercase" style={{ color: 'var(--ink-soft)' }}>{label}</span>
      </div>
      <div className="px-6 py-7">{children}</div>
    </div>
  );
}

function BAPanel({ variant, label, items }) {
  const isBefore = variant === 'before';
  return (
    <div className="p-5" style={{ background: isBefore ? '#EDE8E0' : '#FAF0E8' }}>
      <div className="flex items-center gap-1.5 mb-3 text-[10px] tracking-[0.14em] uppercase font-medium" style={{ color: isBefore ? '#8A7A60' : 'var(--terracotta)' }}>
        {isBefore ? <X size={12} /> : <Check size={12} />} {label}
      </div>
      {items.map((it) => (
        <div key={it} className="flex items-start gap-2 text-[12.5px] mb-2 leading-[1.5]" style={{ color: 'var(--ink-soft)' }}>
          <span className="shrink-0" style={{ color: isBefore ? '#8A7A60' : 'var(--terracotta)' }}>{isBefore ? '—' : '→'}</span>
          {it}
        </div>
      ))}
    </div>
  );
}

function ScrollablePhone({ label, sub, image }) {
  return (
    <div className="w-full max-w-[280px]">
      <div
        className="relative rounded-[24px] overflow-hidden shadow-warm"
        style={{
          aspectRatio: '360 / 800',
          background: '#FFFFFF',
          border: '1.5px solid var(--ink)',
        }}
      >
        <div
          className="absolute inset-0 overflow-y-auto"
          style={{ scrollbarWidth: 'thin', WebkitOverflowScrolling: 'touch' }}
        >
          <img src={image} alt={label} className="block w-full h-auto" loading="lazy" />
        </div>
        {/* subtle scroll hint pill */}
        <div
          className="absolute bottom-2.5 left-1/2 -translate-x-1/2 px-2.5 py-1 rounded-full text-[9px] tracking-[0.14em] uppercase pointer-events-none"
          style={{ background: 'rgba(26,18,8,0.78)', color: '#FAF6EE', backdropFilter: 'blur(4px)' }}
        >
          scroll ↕
        </div>
      </div>
      <div className="mt-3 text-center">
        <p className="text-[13px] font-medium" style={{ color: 'var(--ink)' }}>{label}</p>
        <p className="text-[12px] mt-1 leading-snug" style={{ color: 'var(--ink-soft)' }}>{sub}</p>
      </div>
    </div>
  );
}

function ScreenPlaceholder({ icon: Icon, title, children }) {
  return (
    <button
      type="button"
      onClick={() => window.open('https://ritikaghosh.framer.website/works/personalisation', '_blank')}
      className="w-full text-center flex flex-col items-center gap-3 px-6 py-10 rounded-[12px] transition-all duration-200"
      style={{
        background: 'linear-gradient(135deg, #F0E8DC 0%, #E8DDD0 100%)',
        border: '1.5px dashed var(--border)',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--terracotta)'; e.currentTarget.style.background = '#FAF0E8'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'linear-gradient(135deg, #F0E8DC 0%, #E8DDD0 100%)'; }}
    >
      <span className="w-10 h-10 rounded-[10px] flex items-center justify-center" style={{ background: '#F2C4A2' }}>
        <Icon size={18} style={{ color: 'var(--terracotta-deep)' }} />
      </span>
      <span className="font-medium text-[13px]" style={{ color: 'var(--ink)' }}>{title}</span>
      <span className="text-[12px] leading-snug max-w-[280px]" style={{ color: 'var(--ink-soft)' }}>{children}</span>
    </button>
  );
}

function OnboardingScreen({ title, sub, image }) {
  const handleClick = () => window.open('https://ritikaghosh.framer.website/works/personalisation', '_blank');
  return (
    <button
      type="button"
      onClick={handleClick}
      className="text-left rounded-[14px] overflow-hidden transition-all duration-200 group"
      style={{ background: 'var(--bg)', border: '1px solid var(--border)', cursor: 'pointer' }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--terracotta)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; }}
    >
      {image ? (
        <div className="w-full flex items-center justify-center overflow-hidden" style={{ aspectRatio: '3/4', background: '#0033B3' }}>
          <img src={image} alt={title} className="block w-full h-full object-contain" loading="lazy" />
        </div>
      ) : (
        <div
          className="w-full flex flex-col items-center justify-center gap-2"
          style={{
            aspectRatio: '3/4',
            background: 'linear-gradient(135deg, #F0E8DC 0%, #E8DDD0 100%)',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <span className="w-10 h-10 rounded-[10px] flex items-center justify-center" style={{ background: '#F2C4A2' }}>
            <Lock size={18} style={{ color: 'var(--terracotta-deep)' }} />
          </span>
          <span className="font-hand text-[15px]" style={{ color: 'var(--ink-soft)' }}>add {title} screen</span>
        </div>
      )}
      <div className="px-4 py-3.5" style={{ borderTop: '1px solid var(--border)' }}>
        <p className="text-[12.5px] font-medium" style={{ color: 'var(--ink)' }}>{title}</p>
        <p className="text-[11px] mt-0.5" style={{ color: 'var(--ink-soft)' }}>{sub}</p>
      </div>
    </button>
  );
}

function CompRow({ name, desc, image }) {
  return (
    <div className="rounded-[14px] overflow-hidden" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
      <div className="px-5 pt-4 pb-3" style={{ borderBottom: '1px solid var(--border)' }}>
        <p className="font-display text-[18px] leading-none mb-1.5" style={{ color: 'var(--ink)', fontWeight: 600 }}>{name}</p>
        <p className="text-[13px] leading-[1.55]" style={{ color: 'var(--ink-soft)' }}>{desc}</p>
      </div>
      <div className="w-full flex items-center justify-center" style={{ background: 'var(--bg)' }}>
        <img src={image} alt={`${name} screens`} className="block w-full h-auto" loading="lazy" />
      </div>
    </div>
  );
}

function CompCard({ name, badge, desc, image }) {
  return (
    <div className="rounded-[12px] overflow-hidden flex flex-col" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
      {image ? (
        <div className="w-full overflow-hidden" style={{ aspectRatio: '4/3', background: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
          <img src={image} alt={`${name} screens`} className="block w-full h-full object-cover" loading="lazy" />
        </div>
      ) : (
        <div
          className="w-full flex items-center justify-center"
          style={{
            aspectRatio: '4/3',
            background: 'linear-gradient(135deg, #F0E8DC 0%, #E8DDD0 100%)',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <span className="font-hand text-[15px]" style={{ color: 'var(--ink-soft)' }}>
            add {name} screenshots —
          </span>
        </div>
      )}
      <div className="p-4 flex-1">
        <div className="flex items-center gap-2 mb-2 text-[13px] font-medium" style={{ color: 'var(--ink)' }}>
          {name}
          <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ background: '#FAF0E8', color: 'var(--terracotta-deep)' }}>{badge}</span>
        </div>
        <p className="text-[12.5px] leading-[1.55]" style={{ color: 'var(--ink-soft)' }}>{desc}</p>
      </div>
    </div>
  );
}

function Insight({ icon: Icon, children }) {
  return (
    <div className="reveal flex items-start gap-3.5 p-5 rounded-[12px] my-7" style={{ background: 'var(--ink)' }}>
      <Icon size={20} style={{ color: 'var(--ochre)', flexShrink: 0, marginTop: 2 }} />
      <p className="text-[13.5px] leading-[1.65]" style={{ color: '#D4C8B8' }}>{children}</p>
    </div>
  );
}

function PhaseBadge({ variant, icon: Icon, children }) {
  const isP1 = variant === 'phase1';
  const styles = isP1
    ? { background: '#E8F0EA', color: '#4A6B52', border: '1px solid #C5D8C8' }
    : { background: '#E5EBF2', color: '#2A4A6B', border: '1px solid #C0CEDC' };
  return (
    <div className="reveal inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[11px] font-medium tracking-[0.1em] uppercase mb-4" style={styles}>
      <Icon size={13} /> {children}
    </div>
  );
}

function SignalCard({ Icon, title, desc }) {
  return (
    <div className="rounded-[12px] p-4 text-center" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
      <Icon size={22} className="mx-auto mb-2" style={{ color: 'var(--terracotta)' }} />
      <p className="text-[12.5px] font-medium mb-1" style={{ color: 'var(--ink)' }}>{title}</p>
      <p className="text-[11px] leading-[1.5]" style={{ color: 'var(--ink-soft)' }}>{desc}</p>
    </div>
  );
}

function ExpiryCard({ title, fill, sub, image }) {
  return (
    <div className="rounded-[12px] overflow-hidden flex flex-col" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
      {image && (
        <div className="w-full flex items-center justify-center" style={{ aspectRatio: '4 / 5', background: '#FFFFFF', borderBottom: '1px solid var(--border)' }}>
          <img src={image} alt={title} className="block w-full h-full object-contain" loading="lazy" />
        </div>
      )}
      <div className="p-4">
        <p className="text-[13px] font-medium mb-1" style={{ color: 'var(--ink)' }}>{title}</p>
        <div className="h-[6px] rounded-full overflow-hidden my-2.5" style={{ background: 'var(--border)' }}>
          <div className="h-full rounded-full" style={{ width: `${fill}%`, background: 'var(--terracotta)' }} />
        </div>
        <p className="text-[11.5px]" style={{ color: 'var(--ink-soft)' }}>{sub}</p>
      </div>
    </div>
  );
}

function DLStep({ Icon, title, sub, highlight = false, border = false }) {
  return (
    <div
      className="flex-1 p-4 text-center relative"
      style={{
        background: highlight ? '#FAF0E8' : 'var(--bg)',
        borderLeft: border ? '1px solid var(--border)' : 'none',
      }}
    >
      <Icon size={20} className="mx-auto mb-1.5" style={{ color: highlight ? 'var(--terracotta-deep)' : 'var(--ink)' }} />
      <p className="text-[11.5px] font-medium mb-1" style={{ color: 'var(--ink)' }}>{title}</p>
      <p className="text-[10.5px] leading-[1.4]" style={{ color: 'var(--ink-soft)' }}>{sub}</p>
    </div>
  );
}

function LearningCard({ n, children }) {
  return (
    <div className="reveal rounded-[12px] p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
      <p className="font-display leading-none mb-2.5" style={{ fontSize: 28, color: 'var(--ochre)', fontWeight: 600 }}>{n}</p>
      <p className="text-[13.5px] leading-[1.6]" style={{ color: 'var(--ink-soft)' }}>{children}</p>
    </div>
  );
}

function Ornament() {
  return (
    <div className="text-center my-12 tracking-[8px] text-[20px]" style={{ color: 'var(--ochre)' }}>· · ·</div>
  );
}

function UserModelDiagram() {
  return (
    <svg width="100%" viewBox="0 0 620 200" role="img" aria-label="Three-layer user model">
      <defs>
        <marker id="ar2" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
          <path d="M2 1L8 5L2 9" fill="none" stroke="context-stroke" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </marker>
      </defs>
      {/* Contextual */}
      <rect x="10" y="10" width="150" height="30" rx="8" fill="#FAF0E8" stroke="#F2C4A2" />
      <text x="85" y="30" textAnchor="middle" fontFamily="DM Sans, sans-serif" fontSize="11" fontWeight="500" fill="#A4501F">Contextual: time · location</text>
      {/* Declared */}
      <rect x="10" y="50" width="150" height="56" rx="8" fill="#E8F0EA" stroke="#C5D8C8" />
      <text x="85" y="73" textAnchor="middle" fontFamily="DM Sans, sans-serif" fontSize="12" fontWeight="500" fill="#3A5A42">Declared</text>
      <text x="85" y="89" textAnchor="middle" fontFamily="DM Sans, sans-serif" fontSize="11" fill="#5A7A62">Language · Interests</text>
      {/* Behavioural */}
      <rect x="10" y="124" width="150" height="56" rx="8" fill="#E5EBF2" stroke="#C0CEDC" />
      <text x="85" y="147" textAnchor="middle" fontFamily="DM Sans, sans-serif" fontSize="12" fontWeight="500" fill="#2A4A6B">Behavioural</text>
      <text x="85" y="163" textAnchor="middle" fontFamily="DM Sans, sans-serif" fontSize="11" fill="#4A6A8B">Clicks · Dwell · Actions</text>
      {/* Arrows */}
      <line x1="162" y1="25"  x2="244" y2="92"  stroke="#C4622D" strokeWidth="1.5" strokeDasharray="4 3" markerEnd="url(#ar2)" />
      <line x1="162" y1="78"  x2="244" y2="100" stroke="#C4622D" strokeWidth="1.5" strokeDasharray="4 3" markerEnd="url(#ar2)" />
      <line x1="162" y1="152" x2="244" y2="110" stroke="#C4622D" strokeWidth="1.5" strokeDasharray="4 3" markerEnd="url(#ar2)" />
      {/* Profile */}
      <rect x="244" y="68" width="130" height="56" rx="10" fill="#C4622D" />
      <text x="309" y="91"  textAnchor="middle" fontFamily="DM Sans, sans-serif" fontSize="12" fontWeight="500" fill="#FAF6EE">User profile</text>
      <text x="309" y="108" textAnchor="middle" fontFamily="DM Sans, sans-serif" fontSize="11" fill="#FADDCC">Lightweight model</text>
      {/* Output arrows */}
      <line x1="376" y1="96" x2="454" y2="55"  stroke="#9A876A" strokeWidth="1.5" markerEnd="url(#ar2)" />
      <line x1="376" y1="96" x2="454" y2="96"  stroke="#9A876A" strokeWidth="1.5" markerEnd="url(#ar2)" />
      <line x1="376" y1="96" x2="454" y2="137" stroke="#9A876A" strokeWidth="1.5" markerEnd="url(#ar2)" />
      {/* Surfaces */}
      <rect x="456" y="32"  width="120" height="36" rx="7" fill="#F5F0E8" stroke="#D6CEBF" />
      <text x="516" y="55"  textAnchor="middle" fontFamily="DM Sans, sans-serif" fontSize="11" fontWeight="500" fill="#2A1F14">Home dashboard</text>
      <rect x="456" y="78"  width="120" height="36" rx="7" fill="#F5F0E8" stroke="#D6CEBF" />
      <text x="516" y="101" textAnchor="middle" fontFamily="DM Sans, sans-serif" fontSize="11" fontWeight="500" fill="#2A1F14">Search defaults</text>
      <rect x="456" y="124" width="120" height="36" rx="7" fill="#F5F0E8" stroke="#D6CEBF" />
      <text x="516" y="147" textAnchor="middle" fontFamily="DM Sans, sans-serif" fontSize="11" fontWeight="500" fill="#2A1F14">Offers + promotions</text>
    </svg>
  );
}
